<template>
	<div class="line-chart-wrapper" :style="{ height: `${height}px` }">
		<Line :data="chartData" :options="chartOptions" />
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Line } from "vue-chartjs";
import {
	Chart as ChartJS,
	LineController,
	LineElement,
	PointElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
	Filler,
} from "chart.js";

ChartJS.register(
	LineController,
	LineElement,
	PointElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
	Filler,
);

export interface LineDataset {
	label: string;
	data: (number | null)[];
	color: string;
	fill?: boolean;
	tension?: number;
}

export interface ReferenceLine {
	value: number;
	label: string;
	color: string;
	showLabel?: boolean;
}

interface Props {
	labels: string[];
	datasets: LineDataset[];
	height?: number;
	tooltipSuffix?: string;
	valueFormatter?: (value: number) => string;
	tooltipTitles?: string[];
	yAxisLabel?: string;
	referenceLines?: ReferenceLine[];
}

const props = withDefaults(defineProps<Props>(), {
	height: 300,
	tooltipSuffix: "",
	yAxisLabel: "",
});

const chartData = computed(() => {
	const dataDatasets = props.datasets.map((ds) => ({
		label: ds.label,
		data: ds.data,
		borderColor: ds.color,
		backgroundColor: ds.fill ? `${ds.color}20` : ds.color,
		pointBackgroundColor: ds.color,
		pointRadius: 4,
		pointHoverRadius: 6,
		tension: ds.tension ?? 0.3,
		fill: ds.fill ?? false,
		spanGaps: true,
	}));

	const refDatasets = (props.referenceLines ?? []).map((ref) => ({
		label: ref.label,
		data: props.labels.map(() => ref.value),
		borderColor: ref.color,
		backgroundColor: "transparent",
		pointRadius: 0,
		pointHoverRadius: 0,
		borderDash: [8, 4],
		borderWidth: 2,
		tension: 0,
		fill: false,
		spanGaps: true,
	}));

	return {
		labels: props.labels,
		datasets: [...dataDatasets, ...refDatasets],
	};
});

const chartOptions = computed(() => ({
	responsive: true,
	maintainAspectRatio: false,
	interaction: {
		mode: "index" as const,
		intersect: false,
	},
	scales: {
		x: {
			grid: { display: false },
		},
		y: {
			beginAtZero: true,
			title: props.yAxisLabel
				? { display: true, text: props.yAxisLabel, font: { size: 12 } }
				: { display: false },
		},
	},
	plugins: {
		legend: {
			position: "bottom" as const,
			labels: {
				filter: (item: { datasetIndex?: number }) =>
					props.referenceLines?.[(item.datasetIndex ?? -1) - props.datasets.length]?.showLabel !== false,
				padding: 16,
				usePointStyle: true,
				pointStyleWidth: 10,
				font: { size: 12 },
			},
		},
		tooltip: {
			filter: (item: any) => item.raw !== null &&
				props.referenceLines?.[item.datasetIndex - props.datasets.length]?.showLabel !== false,
			callbacks: {
				...(props.tooltipTitles
					? {
							title: (items: any[]) => {
								const idx = items[0]?.dataIndex;
								return props.tooltipTitles?.[idx] ?? props.labels[idx] ?? "";
							},
						}
					: {}),
				...(props.valueFormatter || props.tooltipSuffix
					? {
							label: (ctx: any) =>
								`${ctx.dataset.label}: ${props.valueFormatter ? props.valueFormatter(ctx.parsed.y) : `${ctx.parsed.y}${props.tooltipSuffix}`}`,
						}
					: {}),
			},
		},
	},
}));
</script>

<style scoped>
.line-chart-wrapper {
	position: relative;
}
</style>
